import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { CircularProgress } from 'src/components/CircularProgress';
import { TextField } from 'src/components/TextField';
import { Package, PackageSearchResult } from 'src/models/package/_types';
import { searchPackages } from 'src/models/package/package';

interface PackageAutocompleteProps {
  value?: Package | Package[] | null;
  onChange: (pkg: Package | Package[] | null) => void;
  label?: string;
  autoFocus?: boolean;
  multiple?: boolean;
}

export const PackageAutocomplete: React.FC<PackageAutocompleteProps> = ({
  value,
  onChange,
  label = 'Select Package',
  multiple,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<PackageSearchResult[]>([]);

  const {
    data: packages,
    refetch: refetchPackages,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ['packages', 'search', searchValue],
    queryFn: () => searchPackages({ search: searchValue }),
    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  useEffect(() => {
    if (packages) {
      setOptions(packages);
    }
  }, [packages]);

  const debouncedSearch = useCallback(
    (value: string) => {
      debounce(() => {
        if (value.length >= 2) {
          refetchPackages();
        }
      }, 300)();
    },
    [refetchPackages]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      getOptionLabel={(option: Package) => option.name}
      value={value || (multiple ? [] : null)}
      onChange={(_, newValue) => {
        onChange(multiple ? (newValue as Package[]) : newValue);
      }}
      onInputChange={handleInputChange}
      loading={searchFetching}
      filterOptions={(x) => x}
      noOptionsText={
        searchValue ? 'No packages found' : 'Start typing to find packages'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searchFetching ? (
                  <CircularProgress color="inherit" size="small" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
